import { AuthGuard } from '@/apiConnection/AuthGuard';
import { PanelGuard } from '@/apiConnection/PanelGuard';
import { RoleGuard } from '@/apiConnection/RoleGuard';
import { SuperadminGuard } from '@/apiConnection/SuperadminGuard';
import { SuperadminOnlyGuard } from '@/apiConnection/SuperadminOnlyGuard';
import { LayoutComponent } from '@/layout/layout.component';
import { ClientListPage } from '@/pages/clients/client-list/client-list.page';
import { CompanyListPage } from '@/pages/companies/company-list/company-list.page';
import { CompanyPage } from '@/pages/company/company.page';
import { LoginPage } from '@/pages/login/login.page';
import { ProviderListPage } from '@/pages/providers/provider-list/provider-list.page';
import { Routes } from '@angular/router';
import { CategoryListPage } from '@/pages/categories/category-list/category-list.page';
import { ProductListPage } from '@/pages/products/product-list/product-list.page';
import { ProductRestaurantViewPage } from '@/pages/products/product-restaurant-view/product-restaurant-view.page';
import { ProductStockReportPage } from '@/pages/products/stock-report/stock-report.page';
import { ProductStockReportCostPage } from '@/pages/products/stock-report-cost/stock-report.page';
import { ProductStockReportMediumPricePage } from '@/pages/products/stock-report-medium-price/stock-report.page';
import { SettingsPage } from '@/pages/settings/settings/settings.page';
import { MeasureListPage } from '@/pages/measures/measure-list/measure-list.page';
import { ProductTypeListPage } from '@/pages/product-types/product-type-list/product-type-list.page';
import { TableListPage } from '@/pages/tables/table-list/table-list.page';
import { IntegrationListPage } from '@/pages/integrations/integration-list/integration-list.page';
import { IntegrationDetailPage } from '@/pages/integrations/integration-detail/integration-detail.page';
import { CashClosingListPage } from '@/pages/cash-closings/cash-closing-list/cash-closing-list.page';
import { CashClosingPrintComponent } from '@/pages/cash-closings/cash-closing-print/cash-closing-print.component';
import { BusinessTypeGuard } from '@/apiConnection/BusinessTypeGuard';
import { DocumentListPage } from '@/pages/documents/documents-list/documents-list.page';
import { DocumentFormPage } from '@/pages/documents/documents-form/documents-form.page';
import { DocumentAlbaranFormPage } from '@/pages/documents/document-albaran-form/document-albaran-form.page';
import { InvoiceTemplateComponent } from '@/pages/documents/invoice-template/document.component';
import { InvoiceCreditTemplateComponent } from '@/pages/documents/invoice-credit-template/document.component';
import { DeliveryNoteTemplateComponent } from '@/pages/documents/delivery-note-template/document.component';
import { DeliveryNoteSignTemplateComponent } from '@/pages/documents/delivery-note-sign-template/document.component';
import { RefundTemplateComponent } from '@/pages/documents/refund-template/document.component';
import { DocumentMassiveInvoicesFormPage } from '@/pages/documents/document-massive-invoices-form/document-massive-invoices-form.page';
import { DocumentAcceptBudgetPage } from '@/pages/documents/document-accept-budget/document-accept-budget.page';
import { DocumentConvertToBudgetPage } from '@/pages/documents/document-convert-to-budget/document-convert-to-budget.page';
import { PayMethodsListPage } from '@/pages/pay-methods/pay-method-list/pay-methods-list.page';
import { RolesListPage } from '@/pages/roles/roles-list/roles-list.page';
import { UsersListPage } from '@/pages/users/users-list/users-list.page';
import { DocTypeGuard } from '@/apiConnection/DocTypeGuard';
import { CanDeactivateGuard } from '@/apiConnection/CanDeactivateGuard';
import { PurchaseListPage } from '@/pages/purchases/purchases-list/purchases-list.page';
import { PurchaseFormPage } from '@/pages/purchases/purchases-form/purchases-form.page';
import { PurchaseInvoiceTemplateComponent } from '@/pages/purchases/purchase-invoice-template/purchase-invoice.component';
import { InventoryHistoryListPage } from '@/pages/inventory-history/inventory-history-list/inventory-history-list.page';
import { InventoryHistoryFormPage } from '@/pages/inventory-history/inventory-history-form/inventory-history-form.page';
import { DocumentListClientsPage } from '@/pages/documents/documents-list-clients/documents-list.page';
import { DocumentListProductsPage } from '@/pages/documents/documents-list-products/documents-list.page';
import { DocumentListDeliveryNotesPage } from '@/pages/documents/documents-list-delivery-notes/documents-list.page';
import { DocumentListInvoicesPage } from '@/pages/documents/documents-list-invoices/documents-list.page';
import { ActivityLogListPage } from '@/pages/activity-logs/activity-log-list/activity-log-list.page';
import { BackupPage } from '@/pages/backups/backup.page';
import { DashboardPage } from '@/pages/dashboard/dashboard.page';
import { VerifactuInvoiceListPage } from '@/pages/verifactu/verifactu-invoice-list/verifactu-invoice-list.page';
import { TicketListPage } from '@/pages/tickets/ticket-list/ticket-list.page';
import { TicketFormPage } from '@/pages/tickets/ticket-form/ticket-form.page';
import { TicketTemplateComponent } from '@/pages/tickets/ticket-template/ticket.component';
import { RestaurantTicketFormPage } from '@/pages/tickets/restaurant-ticket-form/restaurant-ticket-form.page';
import { SyncStatusListPage } from '@/pages/sync-status/sync-status-list.page';
import { TabletSignPage } from '@/pages/tablet-sign/tablet-sign.page';

export const routes: Routes = [
    { path: "login", component: LoginPage },
    { path: "invoice/:type/:id", component: InvoiceTemplateComponent },
    { path: "invoice-credit/:type/:id", component: InvoiceCreditTemplateComponent },
    { path: "delivery-note/:type/:id", component: DeliveryNoteTemplateComponent },
    { path: "delivery-note-sign/:id", component: DeliveryNoteSignTemplateComponent },
    { path: "refund/:type/:id", component: RefundTemplateComponent },
    { path: "purchase-invoice/:type/:id", component: PurchaseInvoiceTemplateComponent },
    { path: "ticket/:type/:id", component: TicketTemplateComponent },
    { path: "cash-closing/print/:id", component: CashClosingPrintComponent },
    { path: "tablet-sign", component: TabletSignPage },
    {
        path: "",
        canActivate: [AuthGuard],
        component: LayoutComponent,
        children: [
            { path: "", component: DashboardPage, canActivate: [SuperadminGuard] },
            { path: "sync-status", component: SyncStatusListPage, canActivate: [SuperadminGuard] },

            // ── Admin/meta routes (no company context needed) ──────────────
            { path: "settings",  component: SettingsPage },
            { path: "companies", component: CompanyListPage },
            { path: "company",   component: CompanyPage },
            { path: "roles",     component: RolesListPage,  canActivate: [RoleGuard, SuperadminGuard] },
            { path: "users-list", component: UsersListPage, canActivate: [RoleGuard, SuperadminGuard] },

            // ── Data routes (superadmin needs company context) ─────────────
            {
                path: "clients",
                component: ClientListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'clients' },
            },
            {
                path: "providers",
                component: ProviderListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'providers' },
            },
            {
                path: "categories",
                component: CategoryListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'categories' },
            },
            {
                path: "products",
                component: ProductListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'products' },
            },
            {
                path: "products-restaurant",
                component: ProductRestaurantViewPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'products' },
            },
            {
                path: "stock-report",
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'products' },
                children: [
                    { path: "",              component: ProductStockReportPage },
                    { path: "cost",          component: ProductStockReportCostPage },
                    { path: "medium-price",  component: ProductStockReportMediumPricePage },
                    { path: "**", redirectTo: "" },
                ],
            },
            {
                path: "payment-methods",
                component: PayMethodsListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'pay_methods' },
            },
            {
                path: "measures",
                component: MeasureListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'measurements' },
            },
            {
                path: "product-types",
                component: ProductTypeListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'product_types' },
            },
            {
                path: "tables",
                component: TableListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'tables' },
            },
            {
                path: "integrations",
                component: IntegrationListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'integrations' },
            },
            {
                path: "integrations/:id",
                component: IntegrationDetailPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'integrations' },
            },
            {
                path: "cash-closings",
                component: CashClosingListPage,
                canActivate: [SuperadminGuard, PanelGuard, BusinessTypeGuard],
                data: { panel: 'cash_closings', businessType: 'store' },
            },
            {
                path: "budgets",
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'documents' },
                children: [
                    { path: "",              component: DocumentListPage,        data: { docType: 'presupuesto' } },
                    { path: "accept-budget", component: DocumentAcceptBudgetPage, canActivate: [PanelGuard],               data: { panel: 'documents', action: 'can_create' } },
                    { path: "new/:type",     component: DocumentFormPage,        canActivate: [DocTypeGuard, PanelGuard], canDeactivate: [CanDeactivateGuard], data: { panel: 'documents', action: 'can_create' } },
                    { path: "edit/:id",      component: DocumentFormPage,        canActivate: [PanelGuard],               canDeactivate: [CanDeactivateGuard], data: { panel: 'documents', action: 'can_update' } },
                    { path: "view/:id",      component: DocumentFormPage,        canActivate: [PanelGuard],               data: { panel: 'documents', action: 'can_read', readonly: true } },
                    { path: "**", redirectTo: "" },
                ],
            },
            {
                path: "sales",
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'documents' },
                children: [
                    { path: "new/:type",                  component: DocumentFormPage,                canActivate: [DocTypeGuard, PanelGuard], canDeactivate: [CanDeactivateGuard], data: { panel: 'documents', action: 'can_create' } },
                    { path: "edit/:id",                   component: DocumentFormPage,                canActivate: [PanelGuard],               canDeactivate: [CanDeactivateGuard], data: { panel: 'documents', action: 'can_update' } },
                    { path: "albaran/new",                component: DocumentAlbaranFormPage,         canActivate: [DocTypeGuard, PanelGuard], data: { docType: 'factura', panel: 'documents', action: 'can_create' } },
                    { path: "massive-invoices/new",       component: DocumentMassiveInvoicesFormPage, canActivate: [DocTypeGuard, PanelGuard], data: { docType: 'factura', panel: 'documents', action: 'can_create' } },
                    { path: "massive-invoices/new/:type", component: DocumentMassiveInvoicesFormPage, canActivate: [DocTypeGuard, PanelGuard], data: { docType: 'factura', panel: 'documents', action: 'can_create' } },
                    { path: "convert-to-budget",          component: DocumentConvertToBudgetPage,     canActivate: [DocTypeGuard, PanelGuard], data: { docType: 'presupuesto', panel: 'documents', action: 'can_update' } },
                    { path: "**", redirectTo: "" },
                ],
            },
            {
                path: "sales-reports",
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'documents' },
                children: [
                    { path: "",                    component: DocumentListPage },
                    { path: "list-clients",        component: DocumentListClientsPage },
                    { path: "list-products",       component: DocumentListProductsPage },
                    { path: "list-delivery-notes", component: DocumentListDeliveryNotesPage },
                    { path: "list-invoices",       component: DocumentListInvoicesPage },
                    { path: "edit/:id",            component: DocumentFormPage, canActivate: [PanelGuard], canDeactivate: [CanDeactivateGuard], data: { panel: 'documents', action: 'can_update' } },
                    { path: "view/:id",            component: DocumentFormPage, canActivate: [PanelGuard], data: { panel: 'documents', action: 'can_read', readonly: true } },
                    { path: "**", redirectTo: "" },
                ],
            },
            {
                path: "purchase",
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'purchases' },
                children: [
                    { path: "",          component: PurchaseListPage },
                    { path: "new/:type", component: PurchaseFormPage, canActivate: [PanelGuard], data: { panel: 'purchases', action: 'can_create' } },
                    { path: "edit/:id",  component: PurchaseFormPage, canActivate: [PanelGuard], data: { panel: 'purchases', action: 'can_update' } },
                    { path: "**", redirectTo: "" },
                ],
            },
            {
                path: "tickets",
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'tickets' },
                children: [
                    { path: "",           component: TicketListPage },
                    { path: "new",        component: TicketFormPage,           canActivate: [PanelGuard], data: { panel: 'tickets', action: 'can_create' } },
                    { path: "restaurant", component: RestaurantTicketFormPage, canActivate: [PanelGuard], data: { panel: 'tickets', action: 'can_create' } },
                    { path: "edit/:id",   component: TicketFormPage,           canActivate: [PanelGuard], data: { panel: 'tickets', action: 'can_update' } },
                    { path: "**", redirectTo: "" },
                ],
            },
            {
                path: "inventory-history",
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'history_inventory' },
                children: [
                    { path: "",         component: InventoryHistoryListPage },
                    { path: "new",      component: InventoryHistoryFormPage, canActivate: [PanelGuard], data: { panel: 'history_inventory', action: 'can_create' } },
                    { path: "view/:id", component: InventoryHistoryFormPage },
                    { path: "**", redirectTo: "" },
                ],
            },
            {
                path: "activity-logs",
                component: ActivityLogListPage,
                canActivate: [SuperadminOnlyGuard, SuperadminGuard, PanelGuard],
                data: { panel: 'activity_logs' },
            },
            {
                // No `panel:` gate on the backend (POST /api/backups/run is superadmin-only via
                // token.role, not a role_panels permission) - SuperadminOnlyGuard alone mirrors
                // that. Not company-scoped either, so no SuperadminGuard company-selection gate.
                path: "backups",
                component: BackupPage,
                canActivate: [SuperadminOnlyGuard],
            },
            {
                path: "verifactu",
                component: VerifactuInvoiceListPage,
                canActivate: [SuperadminGuard, PanelGuard],
                data: { panel: 'documents' },
            },

            { path: "**", redirectTo: "" },
        ],
    },

    { path: "**", redirectTo: "login" },
];
